/**
 * 템플릿 계약 — 템플릿 종류와 각 템플릿이 받는 데이터의 형태.
 *
 * 템플릿은 **이미지도 영상도 아닌 세 번째 콘텐츠**다. 디자인은 고정돼 있고 내용만
 * 바뀐다. 운영자가 파워포인트를 만들어 이미지로 내보내는 대신, 폼에 값을 채우면
 * 화면이 나온다 — 사내 공지·일정·실적처럼 **자주 바뀌는 내용**에는 이쪽이 맞다.
 *
 * 이 스키마가 유일한 진실원천이다:
 *   - 서버가 저장 전에 검증한다
 *   - 플레이어가 이 타입으로 렌더한다
 *   - 콘솔이 이 타입으로 폼을 만든다
 *
 * 셋이 같은 정의를 쓰지 않으면, 콘솔에서 저장한 것이 화면에서 다르게 나오거나
 * 아예 안 나오는 사고가 난다. `@signageone/core` 에 두는 이유가 그것이다.
 *
 * 출처: `press-center` 프로토타입의 뷰어 템플릿 5종. 반반 화면은 2026-10-30, 무재해 현황·구역 나누기는 2026-11-03 에 더했다.
 */
import { z } from 'zod';
import { BRAND_FONT_ID } from './brand.js';
import { TEMPLATE_FONT_IDS } from './fonts.js';
import { ICON_NAMES } from './icons.js';
/**
 * 템플릿 종류.
 *
 * **id 는 11자까지다.** 처음 다섯 밖의 템플릿은 화면이 하트비트에 `template:<id>` 로 알리는데(`templateCapability`), 이미
 * 깔린 서버는 그 이름을 20자까지만 받고 **하나라도 넘으면 목록 전체를 버린다**(`schemas.ts` 의 `content_types`) — 새
 * 에이전트가 옛 서버에 붙으면 올린 HTML 까지 못 받게 된다. 무재해 현황이 `zero-accident`(22자)였다가 `safety` 가 됐다.
 */
export const TEMPLATE_IDS = [
    'announcement',
    'promotion',
    'alert',
    'schedule',
    'dashboard',
    'split',
    'safety',
    'zones',
];
/**
 * 처음부터 있던 템플릿 — 모든 화면이 그릴 줄 안다. 그 뒤에 더한 템플릿은 그릴 줄 아는 화면에만 보낸다(`templateCapability`).
 */
export const BASE_TEMPLATE_IDS = ['announcement', 'promotion', 'alert', 'schedule', 'dashboard'];
/**
 * 이 템플릿을 받으려면 화면이 하트비트 `content_types` 에 알려야 하는 이름(2026-10-30) — 처음 다섯은 null.
 *
 * 옛 화면은 모르는 템플릿을 받으면 렌더 입구의 스키마에서 실패하고, 세 번 실패하면 그 콘텐츠를 격리해 건너뛴다(Apple TV 는
 * "모르는 템플릿" 안내를 띄운다). 그래서 서버가 해석에서 미리 뺀다 — 올린 HTML 을 알린 화면에만 보내는 것과 같은 길이다.
 */
export function templateCapability(id) {
    return BASE_TEMPLATE_IDS.includes(id) ? null : `template:${id}`;
}
/**
 * 반반 화면의 그림 칸에 올린 HTML 을 띄울 줄 안다는 표시(2026-11-03, 19자). 반반 화면을 그리던 화면(에이전트 1.1.x)은
 * `media.kind: 'html'` 을 몰라 렌더 입구의 스키마에서 실패한다 — 그 내용은 이것까지 알린 화면에만 간다(`templateCapabilities`).
 */
export const SPLIT_HTML_CAPABILITY = 'template:split-html';
/** 하트비트 `content_types` 에 올 수 있는 템플릿 이름 — `template:split` 처럼. */
export const TEMPLATE_CAPABILITIES = [
    ...TEMPLATE_IDS.flatMap((id) => templateCapability(id) ?? []),
    SPLIT_HTML_CAPABILITY,
];
export const templateIdSchema = z.enum(TEMPLATE_IDS);
const iconSchema = z.enum(ICON_NAMES);
/** 표시용 짧은 문자열. 사이니지는 멀리서 읽으므로 길면 어차피 안 읽힌다. */
const line = (max) => z.string().trim().min(1).max(max);
/**
 * 하단 티커.
 *
 * 모든 템플릿이 갖는다 — 사이니지에서 티커는 "지금 이 화면의 주제와 별개로
 * 알려야 하는 것"을 놓는 자리이고, 그 필요는 템플릿 종류와 무관하다.
 */
const tickerSchema = z.object({
    label: line(12).default('NEWS'),
    content: z.string().trim().min(1).max(600),
});
/** 공지사항 — 인사·총무 공지. 큰 제목 하나 + 최근 공지 목록. */
export const announcementDataSchema = z.object({
    badge: line(20).default('공지'),
    title: line(80),
    body: z.string().trim().max(400).default(''),
    date: line(20),
    author: line(30),
    notices: z
        .array(z.object({ title: line(60), dept: line(20) }))
        .max(6)
        .default([]),
    weather: z
        .object({ temperature: line(10), condition: line(20), place: line(30) })
        .nullable()
        .default(null),
    ticker: tickerSchema,
});
/** 프로모션 — 복지·이벤트 홍보. 좌측 비주얼 + 우측 혜택·마감 카운트다운. */
export const promotionDataSchema = z.object({
    badge: line(30).default('이벤트'),
    title: line(60),
    /** 제목 중 강조할 뒷부분. 두 줄로 끊어 읽히게 한다. 비우면 그리지 않는다. */
    titleAccent: z.string().trim().max(60).default(''),
    body: z.string().trim().max(300).default(''),
    amountLabel: line(30).default('연간 지원금'),
    amount: line(30),
    benefits: z
        .array(z.object({ icon: iconSchema, title: line(40), description: line(60) }))
        .min(1)
        .max(4),
    /**
     * 신청 마감 시각. 플레이어가 **주입된 현재 시각**으로 남은 시간을 계산한다.
     * 서버가 계산해 내려주면 폴링 주기만큼 어긋나므로 클라이언트에서 센다.
     */
    deadline: z.coerce.date().nullable().default(null),
    deadlineLabel: line(30).default('신청 마감까지'),
    /**
     * QR 코드 안내. 현재는 **장식용 패턴**을 그린다(실제 인코딩 아님).
     * 실제 QR 생성은 후속 과제 — 스캔되는 QR 이 필요하면 이 필드를 URL 로 받아
     * 인코더를 붙인다.
     */
    qr: z.object({ caption: line(30), hint: line(40) }).nullable().default(null),
    ticker: tickerSchema,
});
/** 긴급알림 — 점검·재난 공지. 어두운 배경 + 행동 지침. */
export const alertDataSchema = z.object({
    label: line(20).default('긴급 알림'),
    iconLabel: line(20).default('점검'),
    title: line(80),
    /** 시간·소요처럼 나란히 두는 수치. 2개까지 — 3개부터는 멀리서 안 읽힌다. */
    stats: z
        .array(z.object({ label: line(20), value: line(30) }))
        .min(1)
        .max(2),
    actions: z.array(line(60)).min(1).max(4),
    ticker: tickerSchema,
});
/** 일정/이벤트 — 오늘 일정 + 다가오는 행사. */
export const scheduleDataSchema = z.object({
    heading: line(30).default('오늘의 일정'),
    events: z
        .array(z.object({
        time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM 형식이어야 합니다'),
        title: line(60),
        location: line(60),
    }))
        .min(1)
        .max(6),
    countdown: z.object({ label: line(20), title: line(30) }).nullable().default(null),
    upcoming: z
        .object({
        label: line(30).default('다가오는 이벤트'),
        title: line(40),
        items: z.array(z.object({ icon: iconSchema, text: line(60) })).max(4),
    })
        .nullable()
        .default(null),
    stats: z
        .object({ heading: line(30), items: z.array(z.object({ value: line(10), label: line(20) })).max(4) })
        .nullable()
        .default(null),
    ticker: tickerSchema,
});
/** 대시보드 — KPI·차트·순위. 실적 현황용. */
export const dashboardDataSchema = z.object({
    kpis: z
        .array(z.object({
        label: line(30),
        value: line(20),
        change: line(20),
        /** 상승이면 초록 화살표, 하락이면 빨강. 색만으로 구분하지 않고 화살표도 바꾼다. */
        up: z.boolean().default(true),
        icon: iconSchema,
        tone: z.enum(['blue', 'green', 'purple', 'orange']).default('blue'),
    }))
        .min(1)
        .max(4),
    chart: z.object({
        title: line(40),
        categories: z.array(line(10)).min(1).max(12),
        series: z
            .array(z.object({
            name: line(20),
            tone: z.enum(['blue', 'green', 'purple', 'orange']),
            /** 0~100 로 정규화된 값. 축 라벨이 없으므로 절대값은 의미가 없다. */
            values: z.array(z.number().min(0).max(100)),
        }))
            .min(1)
            .max(2),
    }),
    leaderboard: z
        .object({
        title: line(30),
        live: z.boolean().default(false),
        rows: z.array(z.object({ name: line(30), dept: line(30), score: line(20) })).max(5),
    })
        .nullable()
        .default(null),
    progress: z
        .object({
        title: line(30),
        rows: z.array(z.object({ name: line(40), percent: z.number().min(0).max(100) })).max(5),
    })
        .nullable()
        .default(null),
    ticker: tickerSchema,
});
/** 콘텐츠로 올린 자산의 id(MongoDB ObjectId 꼴). */
const assetIdSchema = z.string().regex(/^[0-9a-f]{24}$/, '콘텐츠 id 형식이 아닙니다');
/**
 * 고정 크기 HTML 에서 보일 영역(2026-11-03) — 기준 크기에 대한 비율(0~1). 그 영역이 칸에 들어오게 키워 맞춘다(`htmlAreaBox`).
 * 1920×1080 으로 만든 현황판의 한 부분만 반쪽 칸에 크게 보일 때. 늘고 주는 HTML 은 칸 크기에 맞춰 다시 짜여 쓰지 않는다.
 */
export const htmlAreaSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0.05).max(1),
    h: z.number().min(0.05).max(1),
})
    .refine((area) => area.x + area.w <= 1.0001 && area.y + area.h <= 1.0001, '영역이 HTML 밖으로 나갑니다');
/** 반반 화면에서 그림 칸이 차지하는 몫(%) — 나머지가 글 칸이다(2026-11-03). */
export const SPLIT_SHARES = ['40', '50', '60', '70'];
/**
 * 템플릿이 가리키는 콘텐츠 하나 — 반반 화면의 그림 칸, 구역 나누기의 구역에 든 한 장(2026-11-03).
 *
 * 그림·영상·HTML 은 문서에 품지 않고 **콘텐츠로 올린 것**을 가리킨다. 화면은 편성을 받을 때 그 파일도 함께 받아 두고
 * (재생 항목의 `media`) 로컬 주소로 그린다.
 */
export const templateMediaSchema = z.object({
    assetId: assetIdSchema,
    /**
     * 그리는 쪽이 자산을 묻지 않고 `<img>`·`<video>`·HTML 칸을 고르게 함께 적는다. 서버가 저장할 때 자산의 종류와 맞춰 본다.
     */
    kind: z.enum(['image', 'video', 'html']),
    /** 꽉 채우기(넘치는 곳은 잘림) · 다 보이기(남는 곳은 검게). HTML 은 고정 크기로 만든 것에만 — 늘고 주는 HTML 은 칸에 맞춰 다시 짜인다. */
    fit: z.enum(['cover', 'contain']).default('cover'),
    /** HTML 에서 보일 영역(2026-11-03) — 비우면 전체. 고정 크기 HTML 에만 뜻이 있다(`htmlAreaSchema`). */
    area: htmlAreaSchema.nullable().default(null),
});
/**
 * 반반 화면(2026-10-30) — 화면을 반으로 나눠 한쪽엔 그림·영상, 반대쪽엔 글.
 *
 * 그림·영상은 문서에 품지 않고 **콘텐츠로 올린 것**을 가리킨다(`media.assetId`). 화면은 편성을 받을 때 그 파일도 함께 받아
 * 두고(재생 항목의 `media`) 로컬 주소로 그린다 — 인터넷이 끊겨도 돈다. 고르지 않았거나 아직 못 받았으면 장식 무늬다.
 *
 * 그림 칸에는 **올린 HTML** 도 넣을 수 있다(2026-11-03, `kind: 'html'`) — 화면은 그 칸에 올린 HTML 항목처럼 샌드박스로 띄우고
 * (그 HTML 이 품은 영상까지 함께 받는다), 고정 크기로 만든 HTML 은 그 크기로 그려 칸에 맞춘다(`fit`). 이 내용은
 * `template:split-html` 을 알린 화면에만 간다.
 */
export const splitDataSchema = z.object({
    /** 나누는 방향 — 화면에 맞춰(가로 화면은 좌우 · 세로 화면은 위아래) · 좌우 · 위아래. */
    layout: z.enum(['auto', 'columns', 'rows']).default('auto'),
    /** 그림 자리 — 앞(왼쪽·위) · 뒤(오른쪽·아래). */
    mediaSide: z.enum(['start', 'end']).default('start'),
    /**
     * 그림 칸의 몫(2026-11-03) — 40·50·60·70(%). 1920×1080 으로 만든 HTML 이 반쪽 칸에서 너무 작아질 때 그림 칸을 키운다.
     * 이것을 모르는 화면(1.1.x)은 반반(50)으로 그린다 — 칸이 깨지지는 않는다.
     */
    share: z.enum(SPLIT_SHARES).default('50'),
    media: templateMediaSchema.nullable().default(null),
    /** 비우면 배지를 그리지 않는다. */
    badge: z.string().trim().max(20).default(''),
    title: line(60),
    body: z.string().trim().max(300).default(''),
    /** 핵심 몇 줄 — 체크 표시와 함께. */
    points: z.array(line(40)).max(4).default([]),
    /** 글 칸 맨 아래 한 줄(문의처·담당 등). 비우면 그리지 않는다. */
    note: z.string().trim().max(40).default(''),
    ticker: tickerSchema,
});
/** 무재해 목표 배수 — 고를 수 있는 값. 1~5 배수는 한 해씩, 그 뒤는 사이가 벌어진다(5→7 은 두 해, 7→10 은 세 해 …). */
export const ZERO_ACCIDENT_MULTIPLIERS = ['1', '2', '3', '4', '5', '7', '10', '15', '20'];
/** `YYYY-MM-DD` 가 달력에 있는 날인가(2월 30일 같은 날을 거른다). */
function isCalendarDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
/**
 * 무재해 현황(2026-11-03) — 무재해 시작일과 목표 배수로 목표 기간·달성 예정일·달성 일수·달성률·D-day 를 **화면이 그날 날짜로**
 * 센다. 운영자는 시작일과 배수만 정하고, 날이 바뀌어도 고칠 것이 없다.
 *
 * 목표 기간은 앞 배수를 채운 다음 날부터 이 배수를 채우는 날까지다 — 3배수면 시작일 + 2년 ~ 시작일 + 3년 - 1일. 고객사 현황판
 * HTML(과천/마곡 환경안전팀)의 셈을 그대로 옮겼다(`render.ts` 의 `zeroAccidentFigures`).
 */
export const zeroAccidentDataSchema = z.object({
    /** 비우면 배지를 그리지 않는다. */
    badge: z.string().trim().max(20).default('무재해'),
    title: line(40).default('무재해 달성 현황'),
    /** 제목 아래 — 맡은 부서·사업장. 비우면 그리지 않는다. */
    team: z.string().trim().max(30).default(''),
    /** 무재해 시작일 — 화면의 시간대로 센다. */
    startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD 형식이어야 합니다')
        .refine(isCalendarDate, '달력에 없는 날짜입니다'),
    multiplier: z.enum(ZERO_ACCIDENT_MULTIPLIERS).default('1'),
    ticker: tickerSchema,
});
/**
 * 구역 나누기의 모양(2026-11-03) — 둘로 똑같이 · 크게 하나 + 옆 하나 · 크게 하나 + 옆 둘 · 셋으로 똑같이 · 넷으로(2×2).
 * 구역의 자리와 크기는 `styles.ts` 의 `ZONE_BOXES` 다(가로·세로 화면마다). 첫 구역이 큰 구역이다.
 */
export const ZONE_LAYOUTS = ['halves', 'main-side', 'main-pair', 'thirds', 'quad'];
/** 모양마다의 구역 수. */
export const ZONE_COUNTS = {
    halves: 2,
    'main-side': 2,
    'main-pair': 3,
    thirds: 3,
    quad: 4,
};
/** 구역 하나에 돌려 보일 수 있는 장 수. */
export const ZONE_MAX_ITEMS = 10;
/**
 * 한 화면의 영상·HTML 상한 — 납품 기기(안드로이드 5.1·1GB 셋톱)는 영상 둘을 함께 풀지 못하고, HTML 은 칸마다 남의 스크립트가
 * 플레이어와 같은 스레드에서 돈다. 영상은 그 구역에 혼자 둔다(돌려 보이면 끝까지 못 보고 잘린다).
 */
export const ZONE_MAX_VIDEOS = 1;
export const ZONE_MAX_HTML = 2;
/**
 * 구역 하나(2026-11-03) — 그림·영상·HTML(`media`, 여러 장이면 `dwellSec` 마다 넘긴다 · 비우면 장식 무늬) 또는 글(`text`).
 */
export const zoneSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('media'),
        items: z.array(templateMediaSchema).max(ZONE_MAX_ITEMS).default([]),
        /** 여러 장일 때 한 장을 보이는 시간(초). 화면은 시각으로 차례를 정한다 — 같은 내용을 트는 화면끼리 함께 넘어간다. */
        dwellSec: z.number().int().min(3).max(600).default(10),
    }),
    z.object({
        kind: z.literal('text'),
        /** 비우면 배지를 그리지 않는다. */
        badge: z.string().trim().max(20).default(''),
        title: line(60),
        body: z.string().trim().max(300).default(''),
    }),
]);
/**
 * 구역 나누기(2026-11-03) — 화면을 2~4 구역으로 나눠 구역마다 그림·영상·HTML 을 돌려 보이거나 글을 놓는다. 머리글(시계)·
 * 아래 티커는 다른 템플릿과 같다. 반반 화면의 그림 칸이 하나뿐이던 것을 넓힌 것이다.
 *
 * 구역 수는 모양과 같아야 한다. 영상은 한 화면에 하나(그 구역에 혼자), HTML 은 둘까지(`ZONE_MAX_*`).
 */
export const zonesDataSchema = z
    .object({
    layout: z.enum(ZONE_LAYOUTS).default('main-side'),
    zones: z.array(zoneSchema).min(1).max(4),
    ticker: tickerSchema,
})
    .superRefine((data, ctx) => {
    const count = ZONE_COUNTS[data.layout];
    if (data.zones.length !== count) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['zones'], message: `이 모양은 구역이 ${count}개입니다` });
    }
    let videos = 0;
    let html = 0;
    data.zones.forEach((zone, index) => {
        if (zone.kind !== 'media')
            return;
        const own = zone.items.filter((item) => item.kind === 'video').length;
        if (own > 0 && zone.items.length > 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['zones', index, 'items'],
                message: '영상은 그 구역에 혼자 넣습니다 — 다른 그림·HTML 과 돌려 보이면 끝까지 보이지 못합니다',
            });
        }
        videos += own;
        html += zone.items.filter((item) => item.kind === 'html').length;
    });
    if (videos > ZONE_MAX_VIDEOS) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['zones'],
            message: `영상은 한 화면에 ${ZONE_MAX_VIDEOS}개까지입니다 — 화면 기기가 영상 여럿을 함께 풀지 못합니다`,
        });
    }
    if (html > ZONE_MAX_HTML) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['zones'],
            message: `HTML 은 한 화면에 ${ZONE_MAX_HTML}개까지입니다 — 칸마다 스크립트가 돌아 화면 기기가 느려집니다`,
        });
    }
});
/**
 * 템플릿 id 별 데이터 스키마.
 *
 * `discriminatedUnion` 을 쓰면 잘못된 조합(예: alert 인데 kpis 를 보냄)이
 * 저장 시점에 걸린다. 화면에 나간 뒤에 발견하면 늦다.
 */
/**
 * 글꼴 — 템플릿마다 고른다(`fonts.ts`). **없어도 된다** — 글꼴을 넣기 전에 만든 템플릿과
 * 옛 콘솔이 보낸 내용은 기본 글꼴(Pretendard)로 그린다. 저장된 값을 다시 쓰지 않는다.
 *
 * `brand` 는 고객사가 환경 설정에서 올린 사내 글꼴이다(`brand.ts`). 파일이 없거나 아직 받지 못한
 * 화면은 기본 글꼴로 그린다.
 */
export const templateFontSchema = z.enum([...TEMPLATE_FONT_IDS, BRAND_FONT_ID]);
export const templatePayloadSchema = z.discriminatedUnion('id', [
    z.object({ id: z.literal('announcement'), data: announcementDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('promotion'), data: promotionDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('alert'), data: alertDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('schedule'), data: scheduleDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('dashboard'), data: dashboardDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('split'), data: splitDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('safety'), data: zeroAccidentDataSchema, font: templateFontSchema.optional() }),
    z.object({ id: z.literal('zones'), data: zonesDataSchema, font: templateFontSchema.optional() }),
]);
/**
 * 이 내용을 그리려면 화면이 하트비트에 알려야 하는 이름들 — 템플릿 종류의 것(`templateCapability`)에, 반반 화면의 HTML 칸이면
 * `template:split-html` 을 더한다(2026-11-03). 처음 다섯이면 빈 목록.
 */
export function templateCapabilities(payload) {
    const names = [];
    const base = templateCapability(payload.id);
    if (base)
        names.push(base);
    if (payload.id === 'split' && payload.data.media?.kind === 'html')
        names.push(SPLIT_HTML_CAPABILITY);
    return names;
}
/**
 * 템플릿이 가리키는 그림·영상·HTML(2026-10-30) — 반반 화면의 그림 칸, 구역 나누기의 구역마다의 장(2026-11-03). 같은 것은
 * 한 번만, 처음 나온 차례대로. 서버는 저장할 때 이것으로 이 고객사의 그 종류 자산인지 맞춰 본다. 형태가 깨진 내용이면 빈 목록.
 */
export function templateMediaRefs(payload) {
    const parsed = templatePayloadSchema.safeParse(payload);
    if (!parsed.success)
        return [];
    const { data } = parsed;
    const all = data.id === 'split'
        ? data.data.media
            ? [data.data.media]
            : []
        : data.id === 'zones'
            ? data.data.zones.flatMap((zone) => (zone.kind === 'media' ? zone.items : []))
            : [];
    // 같은 콘텐츠를 다른 종류로 적었으면 둘 다 남긴다 — 서버가 틀린 쪽을 짚는다.
    const seen = new Set();
    return all.flatMap(({ assetId, kind }) => {
        const key = `${assetId}:${kind}`;
        return seen.has(key) ? [] : (seen.add(key), [{ assetId, kind }]);
    });
}
/**
 * 템플릿이 품은 그림·영상·HTML 의 자산 id(2026-10-30). 서버는 이것으로 재생 항목에 파일을 싣고(`media`, HTML 이면 그것이 품은
 * 영상까지) 지우기를 막는다(`templateMediaRefs`).
 */
export function templateMediaIds(payload) {
    return [...new Set(templateMediaRefs(payload).map((ref) => ref.assetId))];
}
export const TEMPLATE_META = {
    announcement: {
        id: 'announcement',
        name: '공지사항',
        description: '큰 제목 하나 + 최근 공지 목록 + 날씨',
        useCase: '인사·총무 공지처럼 한 건을 확실히 읽혀야 할 때',
        defaultDwellSec: 15,
    },
    promotion: {
        id: 'promotion',
        name: '프로모션',
        description: '비주얼 + 혜택 목록 + 마감 카운트다운',
        useCase: '복지·이벤트 신청을 유도할 때',
        defaultDwellSec: 20,
    },
    alert: {
        id: 'alert',
        name: '긴급알림',
        description: '어두운 배경 + 시간 배너 + 행동 지침',
        useCase: '점검·장애처럼 즉시 조치가 필요할 때',
        defaultDwellSec: 20,
    },
    schedule: {
        id: 'schedule',
        name: '일정/이벤트',
        description: '오늘 일정 + 다가오는 행사 + 주간 통계',
        useCase: '로비·회의실 앞에서 하루 일정을 안내할 때',
        defaultDwellSec: 15,
    },
    dashboard: {
        id: 'dashboard',
        name: '대시보드',
        description: 'KPI 4종 + 막대 차트 + 순위 + 진행률',
        useCase: '실적·진행 현황을 상시 노출할 때',
        defaultDwellSec: 25,
    },
    split: {
        id: 'split',
        name: '반반 화면',
        description: '이미지·영상·HTML 반 + 제목·설명·핵심 항목 반 (좌우·위아래)',
        useCase: '제품·현장 사진이나 영상, 올린 HTML 화면을 보여 주며 설명할 때',
        defaultDwellSec: 15,
    },
    safety: {
        id: 'safety',
        name: '무재해 현황',
        description: '목표 배수 · 달성 예정일 · 달성 일수 · 달성률 · D-day',
        useCase: '무재해 시작일과 목표 배수만 정해 두고 그날의 달성 현황을 늘 게시할 때',
        defaultDwellSec: 20,
    }, zones: {
        id: 'zones',
        name: '구역 나누기',
        description: '화면을 2~4 구역으로 — 구역마다 이미지·영상·HTML 을 돌려 보이거나 글',
        useCase: '현황판 HTML·홍보 영상·공지 사진처럼 여러 내용을 한 화면에 늘 함께 띄울 때',
        defaultDwellSec: 60,
    },
};
//# sourceMappingURL=schema.js.map